import React, { useCallback, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { useStore } from '../../../store'
import Modal from '../../../ui/atoms/Modal'
import { routes } from '@linqpal/models'
import { isWeb } from '../../../utils/helpers/commonUtils'
import cookies from '../../../utils/service/cooker'
import { InvoiceDetails } from '@linqpal/models/src/mst/Decision'
import SignInStore from '../../Auth/stores/SignInStore'
import SignupStore from '../../Auth/stores/SignupStore'
import SignInVerify from '../../Auth/SignIn/SignInVerify'
import SignUpVerify from '../../Auth/SignUp/SignUpVerify'
import LocalStorage from '../../../store/LocalStorage'
import { useInvoiceDetailsContext } from '../TabInvoice/InvoiceDetailsContext'
import SignInPassword from '../../Auth/SignIn/SignInPassword'
import SignUpHome from '../../Auth/SignUp/SignUpHome'
import SignIn from '../../Auth/SignIn/SignIn'
import SignUpPassword from '../../Auth/SignUp/SignUpPassword'
import SignUpForm from '../../Auth/SignUp/SignUpForm'
import { LOAN_APPLICATION_STATUS } from '@linqpal/models/src/dictionaries'
import ApplicationStore from '../../GeneralApplication/Application/ApplicationStore'
import { useResponsive } from '../../../../../components/src/hooks'
import InvoiceActionsStore from './InvoiceActionsStore'
import { ApplicationType } from '@linqpal/models/src/dictionaries/applicationType'

// noinspection JSCheckFunctionSignatures

interface PropTypes {
  invoice: any
  invoiceIds?: string[]
  visible: boolean
  onClose: () => void
  children?: React.ReactNode
  navigation: any
  btcredit?: boolean
  setLoading?: (a) => void
  status?: string
  route: any
  selectedInvoices?: any[]
}

const BuilderInvoiceSignup = observer(function BuilderInvoiceSignup({
  invoice,
  visible,
  onClose,
  children,
  navigation,
  btcredit,
  setLoading = () => {},
  status = '',
  route,
  invoiceIds,
  selectedInvoices,
}: PropTypes) {
  const store = useStore()
  const session = cookies.get('session')
  const isUserAuthenticated = store.userStore.isAuthenticated
  const { sm } = useResponsive()
  const isPreviouslyApproved =
    store.userStore.settings.get('previouslyApproved')
  const authenticated = (isWeb && session) || isUserAuthenticated
  const { setPaymentPlanModalVisibility } = useInvoiceDetailsContext()
  const { getInvoiceData, setPayAndSubmitApplication, shouldReconnectBank } =
    ApplicationStore
  const {
    takeUserToCreditApp,
    setResumeApplyingForLoan,
    setStartProjectFlow,
    groupedInvoiceProjectNavigation,
    purchaseTypeNavigation,
    singleInvoiceNavigation,
    setReconnectCreditBank,
  } = InvoiceActionsStore

  const showPaymentPlanSelection = useCallback(() => {
    setPayAndSubmitApplication(false)
    const invoiceDetails = InvoiceDetails.create({
      invoiceId: invoiceIds || [invoice._id],
    })
    store.userStore.fetchDocument(ApplicationType.Credit)
    const payment = {
      invoiceDetails,
      method: 'credit',
    }
    getInvoiceData(invoiceIds || [invoice._id])
    LocalStorage.set('payment', payment).then(() =>
      setPaymentPlanModalVisibility(true),
    )
  }, [
    invoice,
    invoiceIds,
    setPaymentPlanModalVisibility,
    store.userStore,
    getInvoiceData,
    setPayAndSubmitApplication,
  ])

  const applyForCredit = useCallback(() => {
    if (
      [
        LOAN_APPLICATION_STATUS.APPROVED,
        LOAN_APPLICATION_STATUS.AUTHORIZED,
        LOAN_APPLICATION_STATUS.PROCESSING,
        // TODO: VK: add PENDING_DISBURSEMENT status when available
      ].find((stat) => stat === status) ||
      (status === LOAN_APPLICATION_STATUS.CANCELED && isPreviouslyApproved)
    ) {
      if (!store.userStore.credit?.LoCnumber) {
        // take user to application to show LoC agreement alert
        takeUserToCreditApp(setLoading, route, navigation, invoice, invoiceIds)
      } else {
        showPaymentPlanSelection() // When user has already applied for credit, and clicks on pay with credit
      }
    } else {
      takeUserToCreditApp(setLoading, route, navigation, invoice, invoiceIds) // First application
    }
  }, [
    store.userStore.credit?.LoCnumber,
    isPreviouslyApproved,
    takeUserToCreditApp,
    showPaymentPlanSelection,
    status,
    invoice,
    invoiceIds,
    navigation,
    route,
    setLoading,
  ])

  const projectFlow = useCallback(() => {
    if (invoiceIds) {
      groupedInvoiceProjectNavigation(sm, selectedInvoices)
    } else {
      singleInvoiceNavigation(invoice, sm, navigation)
    }
  }, [
    invoiceIds,
    groupedInvoiceProjectNavigation,
    singleInvoiceNavigation,
    sm,
    selectedInvoices,
    invoice,
    navigation,
  ])

  useEffect(() => {
    if (btcredit && authenticated && visible) {
      setResumeApplyingForLoan(applyForCredit)
      onClose()
      if (
        shouldReconnectBank &&
        !['', LOAN_APPLICATION_STATUS.NEW].includes(status)
      ) {
        // When there exists a supplier application with finicity bank selected, and user is starting his first loan application dont show reconnect bank screen instead take user into the application form for filling other details like debt etc, so additional check above for status
        setReconnectCreditBank(true)
      } else if (store.userStore.shouldSelectPurchaseType) {
        purchaseTypeNavigation(invoice, selectedInvoices)
        setStartProjectFlow(projectFlow)
      } else if (store.userStore.isPurchaseForProject) {
        projectFlow()
      } else {
        applyForCredit()
      }
    } else if (!children && authenticated && visible) {
      onClose()
    }
  }, [
    status,
    btcredit,
    authenticated,
    selectedInvoices,
    invoice,
    visible,
    onClose,
    children,
    store.userStore.isPurchaseForProject,
    store.userStore.shouldSelectPurchaseType,
    applyForCredit,
    setResumeApplyingForLoan,
    projectFlow,
    purchaseTypeNavigation,
    setStartProjectFlow,
    setReconnectCreditBank,
    shouldReconnectBank,
  ])

  return (
    <>
      {authenticated
        ? children
          ? children
          : null
        : visible && (
            <Modal
              visible={visible}
              onClose={onClose}
              style={{
                flex: 1,
                width: '100%',
                height: '100%',
                margin: 0,
              }}
            >
              <Authenticate
                invoice={invoice}
                onClose={onClose}
                navigation={navigation}
                route={route}
              />
            </Modal>
          )}
    </>
  )
})

export default BuilderInvoiceSignup

const Authenticate = observer(({ invoice, onClose, navigation, route }) => {
  const { canAskOtp, setCodeVerifier, canAskPassword } = SignInStore

  const [isSignIn, setIsSignin] = useState(false)
  const [userChoice, setUserChoice] = useState(false)
  const [goToForm, setGoToForm] = useState(false)

  const dontNavigate = (value) => {
    setCodeVerifier(false)
    setUserChoice(true)
    setIsSignin(value)
  }

  if (invoice.login) {
    SignInStore.setLogin(invoice.login || invoice.phone)
  }

  if (
    (!canAskOtp && !isSignIn && !canAskPassword && userChoice) ||
    (isSignIn && !canAskOtp && !canAskPassword)
  ) {
    return goToForm ? (
      <SignUpForm navigation={navigation} onBack={() => setGoToForm(false)} />
    ) : (
      <SignupOrLogin
        invoice={invoice}
        isSignIn={isSignIn}
        dontNavigate={dontNavigate}
        onClose={onClose}
        route={route}
        navigation={navigation}
        setGoToForm={setGoToForm}
      />
    )
  }

  return canAskOtp ? (
    <SignInVerify
      navigation={navigation}
      dontNavigate={dontNavigate}
      route={route}
      setGoToForm={setGoToForm}
    />
  ) : invoice?.existingCustomer || canAskPassword ? (
    <SignInPassword navigation={navigation} dontNavigate={dontNavigate} />
  ) : goToForm ? (
    <SignUpForm navigation={navigation} onBack={() => setGoToForm(false)} />
  ) : SignupStore.type === 'code' ? (
    <SignUpPhone
      navigation={navigation}
      route={route}
      dontNavigate={dontNavigate}
      invoice={invoice}
      setGoToForm={setGoToForm}
    />
  ) : (
    <SignUpEmail
      navigation={navigation}
      route={route}
      invoice={invoice}
      dontNavigate={dontNavigate}
      setGoToForm={setGoToForm}
    />
  )
})

const SignUpEmail = (props) => {
  const [goToPassword, setGoToPassword] = useState(false)

  return goToPassword ? (
    <SignUpPassword
      {...props}
      onContinue={() => props.setGoToForm(true)}
      onBack={() => setGoToPassword(false)}
    />
  ) : (
    <SignUpHome {...props} onContinue={() => setGoToPassword(true)} />
  )
}

const SignUpPhone = (props) => {
  const [goToOtp, setGoToOtp] = useState(!!SignupStore.codeVerifier)
  return goToOtp && !!SignupStore.codeVerifier ? (
    <SignUpVerify
      {...props}
      onContinue={() => props.setGoToForm(true)}
      onBack={() => setGoToOtp(false)}
    />
  ) : (
    <SignUpHome {...props} onContinue={() => setGoToOtp(true)} />
  )
}

const SignUpUser = observer((props) => {
  const { route, invoice } = props
  const { setValue, setLogin } = SignupStore
  const [type] = useState('')

  const customerAccountId = route?.params?.account
  useEffect(() => {
    if (customerAccountId) {
      routes.customer.getForInvoiceSignup(customerAccountId).then((acc) => {
        setValue('firstName', acc.account?.first_name || '')
        setValue('lastName', acc.account?.last_name || '')
        setValue('email', acc.account?.email || '')
        setValue('phone', acc.account?.phone ? acc.account.phone.slice(2) : '')
        setValue('company_name', acc.account?.name || '')
      })
    } else {
      setValue(
        'firstName',
        invoice?.firstName || invoice?.customer?.first_name || '',
      )
      setValue(
        'lastName',
        invoice.lastName || invoice?.customer?.last_name || '',
      )
      setValue('email', invoice.email || invoice?.customer?.email || '')
      setValue(
        'company_name',
        invoice.company_name || invoice?.customer?.name || '',
      )
      setValue(
        'phone',
        invoice?.customer?.phone ? invoice.customer.phone.slice(2) : '',
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (type) {
      setValue('type', type)
      if (type === 'password') setLogin(SignupStore.email)
      else setLogin(SignupStore.phone)
    }
  }, [type, setLogin, setValue])

  return SignupStore.type === 'code' ? (
    <SignUpPhone {...props} />
  ) : (
    <SignUpEmail {...props} />
  )
})

const SignupOrLogin = ({
  navigation,
  route,
  isSignIn,
  dontNavigate,
  ...rest
}) =>
  !isSignIn ? (
    <SignUpUser
      navigation={navigation}
      route={route}
      dontNavigate={dontNavigate}
      {...rest}
    />
  ) : (
    <SignIn navigation={navigation} dontNavigate={dontNavigate} route={route} />
  )
